// src/components/CancelCommandeCB.js
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import axios from 'axios';

const CancelCommandeCB = () => {
  const [idCommande, setIdCommande] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleCancelOrder = async () => {
    if (!idCommande.trim()) {
      setError('Veuillez entrer un ID de commande valide.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.get(
        `https://lalivraisonv2-api.lalivraison.ma/suivicommande/cancelOrderDispatch?id_commande=${idCommande}`
      );

      // Assuming the response is an object with the UUID as the key
      const orderData = response.data[idCommande];

      if (orderData?.cancellation_success) {
        setSuccess(orderData.message || 'Commande annulée avec succès !');
        setIdCommande('');
      } else {
        setError(
          orderData?.message ||
            'Échec de l\'annulation de la commande. La commande n\'est peut-être pas dans un état annulable.'
        );
      }
    } catch (err) {
      const errorMessage =
        err.response?.status === 404
          ? 'Commande non trouvée. Veuillez vérifier l\'ID de la commande.'
          : err.response?.data?.message ||
            err.response?.data?.error ||
            'Échec de l\'annulation de la commande. Veuillez réessayer.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: 'auto',
        p: 4,
        mt: 4,
        bgcolor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: '#3A3A3A',
          textAlign: 'center',
        }}
      >
        Annuler une Commande
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 3,
          color: '#3A3A3A',
          textAlign: 'center',
          lineHeight: 1.6,
        }}
      >
        Pour annuler une commande, entrez l'ID de commande fourni dans votre confirmation de
        commande. Les commandes peuvent être annulées dans les 5 minutes suivant leur placement ou
        si elles sont encore en attente de confirmation. Si la commande ne peut pas être annulée,
        un message expliquera pourquoi.
      </Typography>

      <TextField
        fullWidth
        label="ID de la Commande"
        value={idCommande}
        onChange={(e) => setIdCommande(e.target.value)}
        variant="outlined"
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: '#21ccbb',
            },
            '&:hover fieldset': {
              borderColor: '#1ab7a8',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#21ccbb',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#3A3A3A',
            '&.Mui-focused': {
              color: '#21ccbb',
            },
          },
        }}
        disabled={loading}
        placeholder="Entrez l'ID de commande (ex. 536671)"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CancelIcon sx={{ color: '#21ccbb' }} />
            </InputAdornment>
          ),
        }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleCancelOrder}
        disabled={loading}
        fullWidth
        sx={{
          py: 1.5,
          bgcolor: '#21ccbb',
          '&:hover': { bgcolor: '#1ab7a8' },
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ color: '#fff' }} />
            Annulation en cours...
          </>
        ) : (
          <>
            <CancelIcon />
            Annuler la Commande
          </>
        )}
      </Button>

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CancelCommandeCB;